import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react'
import { useKeycloak } from '@/hooks/use-keycloak-wrapper'
import { useAuth } from '@/hooks/use-auth'
import { schoolApiService } from '@/services/school.service'
import type { School, SchoolWithStats, UserProfile, AuthToken } from '@/types/school'

interface SchoolContextState {
  // School data
  currentSchool: SchoolWithStats | null
  availableSchools: School[]

  // User data
  currentUser: UserProfile | null
  userRole: string | null

  // Loading states
  isLoadingSchool: boolean
  isLoadingUser: boolean
  isInitialized: boolean

  // Error states
  schoolError: string | null
  userError: string | null

  // Actions
  refreshSchool: () => Promise<void>
  refreshUser: () => Promise<void>
  switchSchool: (schoolId: string) => Promise<void>
  logout: () => void
}

const SchoolContext = createContext<SchoolContextState | undefined>(undefined)

export const useSchoolContext = () => {
  const context = useContext(SchoolContext)
  if (!context) {
    throw new Error('useSchoolContext must be used within a SchoolContextProvider')
  }
  return context
}

interface SchoolContextProviderProps {
  children: ReactNode
}

export const SchoolContextProvider: React.FC<SchoolContextProviderProps> = ({ children }) => {
  const { keycloak, initialized } = useKeycloak()
  const { isAuthenticated, user: authUser } = useAuth()

  // Use either Keycloak or trial auth
  const isUserAuthenticated = isAuthenticated

  // State
  const [currentSchool, setCurrentSchool] = useState<SchoolWithStats | null>(null)
  const [availableSchools, setAvailableSchools] = useState<School[]>([])
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null)
  const [userRole, setUserRole] = useState<string | null>(null)

  // Loading states
  const [isLoadingSchool, setIsLoadingSchool] = useState(false)
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // Error states
  const [schoolError, setSchoolError] = useState<string | null>(null)
  const [userError, setUserError] = useState<string | null>(null)

  // Actions
  // A parent without a school of their own belongs to schools through their
  // children: there is no "current school" to load for them
  const parentWithoutSchool =
    !!authUser && !(authUser as any).school_id && ((authUser as any).roles || []).includes('parent')

  const refreshSchool = useCallback(async () => {
    if (!isUserAuthenticated) return
    if (parentWithoutSchool) {
      setCurrentSchool(null)
      return
    }

    setIsLoadingSchool(true)
    setSchoolError(null)

    try {
      const school = await schoolApiService.getCurrentSchool()
      setCurrentSchool(school)
    } catch (error: any) {
      const status = error?.response?.status ?? error?.status
      if (status === 403 || status === 404) {
        // No school of their own (a parent belongs to schools through their children)
        setCurrentSchool(null)
        return
      }
      console.warn('[SchoolContext] Failed to fetch current school:', error?.message)
      setSchoolError(error.message || 'Failed to load school data')
    } finally {
      setIsLoadingSchool(false)
    }
  }, [isUserAuthenticated, parentWithoutSchool])

  const refreshUser = useCallback(async () => {
    if (!isUserAuthenticated) return

    setIsLoadingUser(true)
    setUserError(null)

    try {
      const user = await schoolApiService.getCurrentUser()
      setCurrentUser(user)
      setUserRole(user.role || user.roles?.[0])
    } catch (error: any) {
      console.error('[SchoolContext] Failed to fetch current user:', error)
      setUserError(error.message || 'Failed to load user data')
    } finally {
      setIsLoadingUser(false)
    }
  }, [isUserAuthenticated])

  const fetchAvailableSchools = useCallback(async () => {
    if (!isUserAuthenticated) return

    try {
      // Check admin access from either Keycloak roles or auth user roles
      const kcRoles = keycloak.tokenParsed?.realm_access?.roles || []
      const userRoles = authUser?.roles || []
      const allRoles = [...kcRoles, ...userRoles]
      const hasAdminAccess = allRoles.includes('admin') || allRoles.includes('superuser')

      if (!hasAdminAccess) {
        return
      }

      const response = await schoolApiService.listSchools(1, 50)
      setAvailableSchools(response.items)
    } catch (error: any) {
      console.error('[SchoolContext] Failed to fetch available schools:', error)
    }
  }, [isUserAuthenticated, keycloak.tokenParsed?.realm_access?.roles, authUser?.roles])

  const switchSchool = useCallback(async (schoolId: string) => {
    setIsLoadingSchool(true)
    setSchoolError(null)

    try {
      await refreshSchool()
      await refreshUser()
    } catch (error: any) {
      console.error('[SchoolContext] Failed to switch school:', error)
      setSchoolError(error.message || 'Failed to switch school')
    } finally {
      setIsLoadingSchool(false)
    }
  }, [refreshSchool, refreshUser])

  const logout = useCallback(() => {
    setCurrentSchool(null)
    setAvailableSchools([])
    setCurrentUser(null)
    setUserRole(null)
    setSchoolError(null)
    setUserError(null)
    setIsInitialized(false)
    keycloak.logout()
  }, [keycloak])

  // Initialize context when user is authenticated (either Keycloak or trial)
  useEffect(() => {
    const initializeContext = async () => {
      if (!isUserAuthenticated) {
        setIsInitialized(false)
        return
      }

      try {
        // Load user and school data in parallel
        await Promise.all([
          refreshUser(),
          refreshSchool(),
          fetchAvailableSchools()
        ])

        setIsInitialized(true)
      } catch (error) {
        console.error('[SchoolContext] Failed to initialize school context:', error)
        setIsInitialized(true) // Still mark as initialized even if there are errors
      }
    }

    initializeContext()
  }, [isUserAuthenticated])

  // Auto-refresh token when it's about to expire (Keycloak only)
  useEffect(() => {
    if (!initialized || !keycloak.authenticated) return

    const refreshToken = async () => {
      try {
        const refreshed = await keycloak.updateToken(30) // Refresh if token expires in 30 seconds
        if (refreshed && keycloak.token) {
          (schoolApiService as any).defaults.headers.common['Authorization'] = `Bearer ${keycloak.token}`
        }
      } catch (error) {
        console.error('[SchoolContext] Failed to refresh token:', error)
        logout()
      }
    }

    // Check for token refresh every 10 seconds
    const interval = setInterval(refreshToken, 10000)

    return () => clearInterval(interval)
  }, [initialized, keycloak.authenticated])

  const contextValue = useMemo<SchoolContextState>(() => ({
    currentSchool,
    availableSchools,
    currentUser,
    userRole,
    isLoadingSchool,
    isLoadingUser,
    isInitialized,
    schoolError,
    userError,
    refreshSchool,
    refreshUser,
    switchSchool,
    logout,
  }), [
    currentSchool,
    availableSchools,
    currentUser,
    userRole,
    isLoadingSchool,
    isLoadingUser,
    isInitialized,
    schoolError,
    userError,
    refreshSchool,
    refreshUser,
    switchSchool,
    logout,
  ])

  return (
    <SchoolContext.Provider value={contextValue}>
      {children}
    </SchoolContext.Provider>
  )
}

// Hook for accessing school-specific data
export const useCurrentSchool = () => {
  const { currentSchool, isLoadingSchool, schoolError, refreshSchool } = useSchoolContext()
  return { currentSchool, isLoadingSchool, schoolError, refreshSchool }
}

// Hook for accessing user data
export const useCurrentUser = () => {
  const { currentUser, userRole, isLoadingUser, userError, refreshUser } = useSchoolContext()
  return { currentUser, userRole, isLoadingUser, userError, refreshUser }
}

// Hook for school management actions
export const useSchoolActions = () => {
  const { switchSchool, logout, refreshSchool, refreshUser } = useSchoolContext()
  return { switchSchool, logout, refreshSchool, refreshUser }
}

export default SchoolContextProvider
